import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {updateColumnGenerationSetup} from 'common/src/modules/contentFeeds/store/contentFeedPage/thunks/updateColumnGenerationSetup.thunk';
import assertAiGeneratedContentFeed, {
  CONTENT_FEED_AI_TOOL_CONTEXT_ERROR,
} from '../utils/assertAiGeneratedContentFeed';
import {ContentFeedAiGeneratedSkillToolName} from '../skills/contentFeedAiGeneratedSkill.enums';
import {generationSetupSchema} from '../utils/generationSetupToolSchemas';
import buildRemoteGenerationSetupFromToolInput from '../utils/buildRemoteGenerationSetupFromToolInput';

const inputSchema = z.object({
  columnKey: z.string().describe('The content feed column key to update generation setup for'),
  generationSetup: generationSetupSchema.describe(
    'The generation setup configuration for this creative column. Only one sub-type (text, image AI, smart crop, product shot) is typically provided.',
  ),
});

export default function updateCreativeColumnGenerationSetupTool(store: CLStore) {
  async function execute({
    input: {columnKey, generationSetup},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const state = store.getState();
    const feed = state.contentFeedPage?.feed;

    if (!assertAiGeneratedContentFeed(feed)) {
      return toolError(CONTENT_FEED_AI_TOOL_CONTEXT_ERROR);
    }

    const columns = feed.feedColumnsSchema?.columns ?? [];
    const column = columns.find((c) => c.columnKey === columnKey);

    if (!column) {
      return toolError(
        `Feed column "${columnKey}" not found. Add the column first (addOrUpdateFeedColumn) or check the column key in the feed schema.`,
      );
    }

    try {
      const remoteGenerationSetup = await buildRemoteGenerationSetupFromToolInput(generationSetup);

      await store
        .dispatch(
          updateColumnGenerationSetup({
            feedId: feed.id,
            request: {columnKey, generationSetup: remoteGenerationSetup},
          }),
        )
        .unwrap();

      return toolResult(
        `Successfully updated generation setup for creative column "${columnKey}".`,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);

      return toolError(`Error updating generation setup: ${errorMessage}`);
    }
  }

  return tool({
    displayName: 'Updating creative column generation setup',
    name: ContentFeedAiGeneratedSkillToolName.UpdateCreativeColumnGenerationSetup,
    description:
      'Updates AI generation setup for a specific creative/output feed column (which input columns participate, image options, text options, etc.).',
    execute,
    inputSchema,
  });
}
